/**
 * Product catalog grid — warehouse list of all sellable SKUs.
 *
 * Server-backed via EntityGrid over odl-app/products/. Columns come from the
 * backend grid-spec descriptor; item_cost is staff-only and gated server-side.
 * Stock-state filter (in_stock/low_stock/out_of_stock), finished-good, obsolete,
 * and BOM filters drive server-side filter[...] params via extraParams. Stats
 * cards (All/In Stock/Low Stock/Out of Stock) fetch from odl-app/products/stats/
 * and recompute when filters change; clicking a card scopes the grid to that
 * stock state. Pull/Receive inventory mutations stay on the legacy warehouse
 * endpoint (POST/PATCH /api/warehouse/inventory/); the grid refreshes after
 * each mutation via the ref captured from onGridReady.
 *
 * Contains:
 * - ProductGrid — EntityGrid over odl-app/products/ (default export)
 */

import {
  AccountTreeOutlined,
  ArchiveOutlined,
  CheckCircleOutline,
  Circle,
  QuestionMark,
  WifiOutlined,
} from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Avatar, Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import { blue, grey, orange, red } from '@mui/material/colors';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { fetchProductsStats } from '@sophocles/api/product';
import { useUser } from '@sophocles/app';
import FilterBar from '@sophocles/components/FilterBar';
import StatCard, { StatCardGroup } from '@sophocles/components/StatCard';
import { EntityGrid, MONEY_FORMAT_OPTIONS } from '@sophocles/data-grids';
import { toJsonApiParams } from '@sophocles/data-grids/datasource/params';
import useColumnVisibilityModel from '@sophocles/data-grids/datasource/useColumnVisibilityModel';
import useGridQuickFilter from '@sophocles/data-grids/datasource/useGridQuickFilter';
import useGridRefreshRef from '@sophocles/data-grids/datasource/useGridRefreshRef';
import useGridStats from '@sophocles/data-grids/datasource/useGridStats';
import { editProductInventory } from '@sophocles/features/warehouse/products/api';
import { productSlice } from '@sophocles/features/warehouse/products/slice';
import { formatNumber } from '@sophocles/utils/formatUtils';
import ProductDetailDrawer from '@sophocles/views/sales/dialogs/ProductDetailDrawer';
import { PullDialog, ReceiveDialog } from '@sophocles/views/sales/dialogs/ProductInventoryDialogs';

import { BOOLEAN_OPTIONS, YesNoCell } from './gridCells';

// Match StatCard segment colors (out / low / in).
function buildStockState(t) {
  return {
    out_of_stock: { color: red[500], label: t('warehouse.product.stockOutOfStock') },
    low_stock: { color: orange[500], label: t('warehouse.product.stockLowStock') },
    in_stock: { color: blue[500], label: t('warehouse.product.stockInStock') },
  };
}

function classifyStock(row) {
  // qty_available is post-debit "available for new orders" — negative when
  // on back order. Backend already nets out qty_on_new_orders, so no math.
  const available = row.qty_available ?? 0;
  if (available <= 0) return 'out_of_stock';
  const threshold = row.low_stock_threshold;
  if (threshold != null && available <= threshold) return 'low_stock';
  return 'in_stock';
}

/**
 * Sales > Products grid.
 *
 * StatCard row segments on stock state (in/low/out) backed by the
 * odl-app/products/stats/ endpoint. The standalone "On Order" column is
 * shown inline; row click opens ProductDetailDrawer.
 */
export default function ProductGrid() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    user,
    permissions: { isShipping, isSalesAdmin },
  } = useUser();
  const canMutateInventory = isShipping || isSalesAdmin;

  const filters = useSelector(state => productSlice.selectors.selectFilters(state));

  // Stable ref for the grid's refresh fn — captured from onGridReady.
  const { onGridReady: captureGridReady, refresh: refreshGrid } = useGridRefreshRef();

  const [stockSegment, setStockSegment] = useState(null);

  // The grid owns the search box, so its committed text is read back through
  // the apiRef; the stat cards count over the same rows the grid lists.
  const gridApiRef = useGridApiRef();
  const [isGridReady, setIsGridReady] = useState(false);
  const [searchText] = useGridQuickFilter(gridApiRef, isGridReady);
  const [activeProduct, setActiveProduct] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Quick-action dialog targets — each holds the product row being acted on, or null.
  const [receiveTarget, setReceiveTarget] = useState(null);
  const [pullTarget, setPullTarget] = useState(null);
  const columnVisibility = useColumnVisibilityModel('productGrid');

  const setFilter = useCallback(
    (key, value) => {
      dispatch(productSlice.actions.setFilters({ ...filters, [key]: value }));
    },
    [dispatch, filters],
  );

  // Default the obsolete filter to false ("No" — hide obsolete SKUs)
  // on first mount. Undefined means the user has never touched it;
  // null is the explicit "Any" choice.
  useEffect(() => {
    if (filters.obsolete === undefined) {
      dispatch(productSlice.actions.setFilters({ ...filters, obsolete: false }));
    }
  }, []);

  // Stock-segment cards drive the backend's filter[stock_state] param
  // (ProductFilter.filter_by_stock_state), which compares the derived
  // Available count against low_stock_threshold to bucket each SKU.
  const extraParams = useMemo(() => {
    const params = {};
    if (stockSegment) params.stock_state = stockSegment;
    if (filters.customer) params.customer = filters.customer;
    if (filters.product_line) params.product_line = filters.product_line;
    if (filters.product_family) params.product_family = filters.product_family;
    if (filters.product_sub_family) params.product_sub_family = filters.product_sub_family;
    if (filters.has_radios === true || filters.has_radios === 'true') params.has_radios = true;
    else if (filters.has_radios === false || filters.has_radios === 'false') params.has_radios = false;
    if (filters.finished_good === true || filters.finished_good === 'true') params.finished_good = true;
    else if (filters.finished_good === false || filters.finished_good === 'false') params.finished_good = false;
    // null = "Any" (no filter). Otherwise send true/false explicitly so
    // the default "hide obsolete" actually narrows the result set.
    if (filters.obsolete === true || filters.obsolete === 'true') params.obsolete = true;
    else if (filters.obsolete === false || filters.obsolete === 'false') params.obsolete = false;
    if (filters.is_bom === true || filters.is_bom === 'true') params.is_bom = true;
    else if (filters.is_bom === false || filters.is_bom === 'false') params.is_bom = false;
    if (filters.used_in_rma === true || filters.used_in_rma === 'true') params.used_in_rma = true;
    if (filters.used_in_packing_supplies === true || filters.used_in_packing_supplies === 'true') {
      params.used_in_packing_supplies = true;
    }
    return params;
  }, [stockSegment, filters]);

  // Stats strip the stock_state filter so counts reflect the other active
  // filters without the current segment narrowing them. The search term is
  // carried so the cards narrow with the grid; toJsonApiParams sends it as
  // the reserved filter[search] key the list request already uses.
  const summaryParams = useMemo(() => {
    const { stock_state: _ss, ...rest } = extraParams;
    return searchText ? { ...rest, text_search: searchText } : rest;
  }, [extraParams, searchText]);

  const statsParams = useMemo(() => toJsonApiParams(summaryParams), [summaryParams]);
  const { stats: summary, isLoading: summaryLoading } = useGridStats(fetchProductsStats, statsParams);

  const handleGridReady = useCallback(
    api => {
      captureGridReady(api);
      setIsGridReady(true);
    },
    [captureGridReady],
  );

  const handleRowClick = useCallback(params => {
    setActiveProduct(params.row);
    setDrawerOpen(true);
  }, []);

  // Pull / Receive handlers mirror ProductDetailDrawer — adjust the row's
  // ProductInventory.qty_on_hand by the dialog input + log an audit entry,
  // then close the dialog and refresh the grid so derived counts update.
  const handleReceive = useCallback(
    ({ source, shipmentNumber, qtyReceived }) => {
      const product = receiveTarget;
      if (!product?.inventory?.id) return;
      const baseQty = (product.components?.length ?? 0) > 0 ? 0 : product.inventory.qty_on_hand;
      const newQty = baseQty + qtyReceived;
      dispatch(
        editProductInventory({
          id: product.inventory.id,
          qty_on_hand: newQty,
          action: 'RECEIVE',
          reason: shipmentNumber
            ? `Received ${qtyReceived}: ${source || 'manual'} with shipment #${shipmentNumber}`
            : source
              ? `Received ${qtyReceived}: ${source}`
              : `Received ${qtyReceived}`,
        }),
      ).then(() => {
        setReceiveTarget(null);
        refreshGrid();
      });
    },
    [dispatch, receiveTarget, refreshGrid],
  );

  const handlePull = useCallback(
    ({ quantityPulled, reason }) => {
      const product = pullTarget;
      if (!product?.inventory?.id) return;
      const newQty = product.inventory.qty_on_hand - quantityPulled;
      dispatch(
        editProductInventory({
          id: product.inventory.id,
          action: 'PULL',
          qty_on_hand: newQty,
          reason: reason ? `Pulled ${quantityPulled}: ${reason}` : `Pulled ${quantityPulled}`,
        }),
      ).then(() => {
        setPullTarget(null);
        refreshGrid();
      });
    },
    [dispatch, pullTarget, refreshGrid],
  );

  const stockState = useMemo(() => buildStockState(t), [t]);

  // Render overrides on top of descriptor column defaults. Only intentional
  // display decisions — sizing, order, and headers come from the descriptor.
  const fieldOverrides = useMemo(
    () => ({
      picture: {
        renderCell: ({ value }) =>
          value ? (
            <img src={value} alt="" style={{ width: 40, height: 40, objectFit: 'cover' }} />
          ) : (
            <Avatar sx={{ width: 40, height: 40, bgcolor: grey[300] }}>
              <QuestionMark fontSize="small" />
            </Avatar>
          ),
      },
      item_part_num: {
        cellClassName: 'cell-pinned',
        renderCell: ({ value, row }) => {
          const state = stockState[classifyStock(row)];
          return (
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Tooltip title={state.label}>
                <Circle sx={{ fontSize: 10, color: state.color }} />
              </Tooltip>
              <Box sx={{ fontFamily: 'monospace' }}>{value}</Box>
            </Stack>
          );
        },
      },
      item_name: {
        renderCell: ({ value, row }) => (
          <Box sx={{ overflow: 'hidden', width: '100%' }}>
            <Typography sx={{ color: 'primary.main' }}>{value}</Typography>
            {row.item_description && (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                {row.item_description}
              </Typography>
            )}
          </Box>
        ),
      },
      qty_warehoused: {
        renderHeader: () => (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <span>{t('warehouse.product.warehousedHeader')}</span>
            <Tooltip title={t('warehouse.product.warehousedTooltip')}>
              <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            </Tooltip>
          </Stack>
        ),
        renderCell: ({ value }) => (value != null ? <Box sx={{ fontFamily: 'monospace' }}>{value}</Box> : null),
      },
      qty_ordered: {
        renderHeader: () => (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <span>{t('warehouse.product.onOrderHeader')}</span>
            <Tooltip title={t('warehouse.product.onOrderTooltip')}>
              <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            </Tooltip>
          </Stack>
        ),
        renderCell: ({ value }) => (
          <Box sx={{ fontFamily: 'monospace', color: value > 0 ? 'text.primary' : 'text.disabled' }}>{value ?? 0}</Box>
        ),
      },
      qty_available: {
        renderHeader: () => (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <span>{t('warehouse.product.availableHeader')}</span>
            <Tooltip title={t('warehouse.product.availableTooltip')}>
              <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            </Tooltip>
          </Stack>
        ),
        renderCell: ({ value: rawValue }) => {
          const value = rawValue ?? 0;
          return (
            <Box
              sx={{
                fontFamily: 'monospace',
                fontWeight: 600,
                color: value < 0 ? 'error.main' : value === 0 ? 'warning.main' : 'text.primary',
              }}
            >
              {value}
            </Box>
          );
        },
      },
      item_price: {
        renderCell: ({ value }) => (
          <Box sx={{ fontFamily: 'monospace' }}>${formatNumber(Number(value) || 0, user, MONEY_FORMAT_OPTIONS)}</Box>
        ),
      },
      item_cost: {
        renderCell: ({ value }) =>
          value != null ? (
            <Box sx={{ fontFamily: 'monospace' }}>${formatNumber(Number(value) || 0, user, MONEY_FORMAT_OPTIONS)}</Box>
          ) : null,
      },
      has_radios: {
        renderCell: ({ value }) => <YesNoCell value={value} />,
      },
      finished_good: {
        renderCell: ({ value }) => <YesNoCell value={value} />,
      },
      obsolete: {
        renderCell: ({ value }) => <YesNoCell value={value} />,
      },
      is_service: {
        renderCell: ({ value }) => <YesNoCell value={value} />,
      },
    }),
    [t, stockState, user],
  );

  // Pull/Receive action buttons — bespoke column the descriptor doesn't own.
  // Buttons stop propagation so the row click (detail drawer) doesn't also fire.
  const extraColumns = useMemo(
    () => [
      {
        field: 'actions',
        headerName: t('actions.actions'),
        width: 170,
        sortable: false,
        filterable: false,
        hideable: false,
        disableColumnMenu: true,
        align: 'right',
        headerAlign: 'right',
        display: 'flex',
        renderCell: ({ row }) => {
          if (!canMutateInventory) return null;
          if (row.obsolete) return null;
          return (
            <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ width: '100%' }}>
              <Button
                size="small"
                variant="outlined"
                onClick={event => {
                  event.stopPropagation();
                  setPullTarget(row);
                }}
                sx={{ minWidth: 0, textTransform: 'none' }}
              >
                {t('warehouse.product.pull')}
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={event => {
                  event.stopPropagation();
                  setReceiveTarget(row);
                }}
                sx={{ minWidth: 0, textTransform: 'none' }}
              >
                {t('warehouse.product.receive')}
              </Button>
            </Stack>
          );
        },
      },
    ],
    [t, canMutateInventory],
  );

  // Inline chips: the two most-used filters. Niche ones go in the
  // More-filters popover.
  const quickFilters = useMemo(
    () => [
      {
        key: 'has_radios',
        label: t('warehouse.product.hasRadios'),
        type: 'single',
        allLabel: 'Any',
        icon: <WifiOutlined sx={{ fontSize: 16 }} />,
        options: BOOLEAN_OPTIONS,
        value: filters.has_radios === true ? 'true' : filters.has_radios === false ? 'false' : null,
        onChange: value => setFilter('has_radios', value === 'true' ? true : value === 'false' ? false : null),
      },
      {
        key: 'finished_good',
        label: t('warehouse.product.finishedGood'),
        type: 'single',
        allLabel: 'Any',
        icon: <CheckCircleOutline sx={{ fontSize: 16 }} />,
        options: BOOLEAN_OPTIONS,
        value: filters.finished_good === true ? 'true' : filters.finished_good === false ? 'false' : null,
        onChange: value => setFilter('finished_good', value === 'true' ? true : value === 'false' ? false : null),
      },
    ],
    [t, filters, setFilter],
  );

  const moreFilters = useMemo(
    () => [
      {
        key: 'obsolete',
        label: t('warehouse.product.obsolete'),
        type: 'single',
        allLabel: 'Any',
        icon: <ArchiveOutlined sx={{ fontSize: 16 }} />,
        options: BOOLEAN_OPTIONS,
        // Default state shows "No" (hide obsolete). null === "Any".
        value:
          filters.obsolete === true || filters.obsolete === 'true'
            ? 'true'
            : filters.obsolete === null
              ? null
              : 'false',
        onChange: value => setFilter('obsolete', value === 'true' ? true : value === 'false' ? false : null),
      },
      {
        key: 'is_bom',
        label: t('warehouse.product.bom'),
        type: 'single',
        allLabel: 'Any',
        icon: <AccountTreeOutlined sx={{ fontSize: 16 }} />,
        options: BOOLEAN_OPTIONS,
        value: filters.is_bom === true ? 'true' : filters.is_bom === false ? 'false' : null,
        onChange: value => setFilter('is_bom', value === 'true' ? true : value === 'false' ? false : null),
      },
    ],
    [t, filters, setFilter],
  );

  const handleResetAll = useCallback(() => {
    setStockSegment(null);
    dispatch(
      productSlice.actions.setFilters({
        customer: null,
        product_line: null,
        product_family: null,
        product_sub_family: null,
        has_radios: null,
        finished_good: null,
        obsolete: false, // default state — hide obsolete SKUs
        is_bom: null,
        used_in_rma: null,
        used_in_packing_supplies: null,
      }),
    );
  }, [dispatch]);

  const segments = useMemo(
    () => [
      {
        value: null,
        label: t('warehouse.product.statAllSkus'),
        count: summary?.all ?? 0,
        tooltip: t('warehouse.product.statAllSkusTooltip'),
      },
      {
        value: 'in_stock',
        label: t('warehouse.product.statInStock'),
        count: summary?.in_stock ?? 0,
        color: blue[500],
        tooltip: t('warehouse.product.statInStockTooltip'),
      },
      {
        value: 'low_stock',
        label: t('warehouse.product.statLowStock'),
        count: summary?.low_stock ?? 0,
        color: orange[500],
        tooltip: t('warehouse.product.statLowStockTooltip'),
      },
      {
        value: 'out_of_stock',
        label: t('warehouse.product.statOutOfStock'),
        count: summary?.out_of_stock ?? 0,
        color: red[500],
        tooltip: t('warehouse.product.statOutOfStockTooltip'),
      },
    ],
    [t, summary],
  );

  const statsHeader = (
    <StatCardGroup columns={{ xs: 2, sm: segments.length }} carouselBelow="md" sx={{ marginBottom: 'var(--page-gap)' }}>
      {segments.map(segment => (
        <StatCard
          key={String(segment.value)}
          title={segment.label}
          value={segment.count}
          color={segment.color}
          tooltip={segment.tooltip}
          loading={summaryLoading}
          active={stockSegment === segment.value}
          onClick={() => setStockSegment(stockSegment === segment.value ? null : segment.value)}
        />
      ))}
    </StatCardGroup>
  );

  const toolbarContent = (
    <FilterBar quickFilters={quickFilters} moreFilters={moreFilters} onResetAll={handleResetAll} />
  );

  return (
    <>
      <EntityGrid
        basePath="products"
        fieldOverrides={fieldOverrides}
        extraParams={extraParams}
        options={{
          searchPlaceholder: t('warehouse.product.searchProducts'),
          persistKey: 'productGrid',
          infiniteScroll: true,
          gridApiRef,
          createAction: canMutateInventory
            ? {
                label: t('warehouse.product.createProduct'),
                onClick: () => navigate('/sales/products/new'),
              }
            : undefined,
          toolbarContent,
          onRowClick: handleRowClick,
          onGridReady: handleGridReady,
          statsHeader,
          extraColumns,
          columnVisibility,
          gridProps: { clickableRows: true },
          countNoun: t('warehouse.product.productsCountNoun'),
        }}
      />

      <ProductDetailDrawer
        product={activeProduct}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onMutated={refreshGrid}
      />

      <ReceiveDialog open={Boolean(receiveTarget)} onClose={() => setReceiveTarget(null)} onReceive={handleReceive} />
      <PullDialog open={Boolean(pullTarget)} onClose={() => setPullTarget(null)} onPull={handlePull} />
    </>
  );
}
